<template>
  <div
    class="loading-page"
    :class="{ 'is-mobile': isMobile }"
    :style="{
      backgroundImage: `url(${isMobile ? bgMobile : bgDesktop}), linear-gradient(180deg, #f0f3fc 0%, #e8eefc 50%, #e3eafc 100%)`,
    }"
  >
    <div class="status">
      <div class="dot-spinner" aria-hidden="true"><span /><span /><span /></div>
      <div class="status-text">{{ message }}</div>
      <div class="percent">{{ percent }}%</div>
      <div class="progress-track">
        <div class="progress-fill" :style="{ width: `${percent}%` }" />
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { onMounted, onUnmounted, ref } from "vue";
import { useResponsive } from "@/composables/useResponsive";
import bgDesktop from "@/assets/loading-pc.png";
import bgMobile from "@/assets/loading-mobile.png";

const props = withDefaults(
  defineProps<{
    message?: string;
    durationMs?: number;
  }>(),
  {
    message: "กำลังเตรียมระบบ...",
    durationMs: 2500,
  },
);

const { isMobile } = useResponsive();
const percent = ref(0);
let timer: number | null = null;

onMounted(() => {
  const start = Date.now();
  const tick = () => {
    const elapsed = Date.now() - start;
    const ratio = Math.min(1, elapsed / props.durationMs);
    const eased = 1 - Math.pow(1 - ratio, 1.6);
    percent.value = Math.min(99, Math.round(eased * 100));
    if (ratio >= 1 && timer != null) {
      window.clearInterval(timer);
      timer = null;
    }
  };
  tick();
  timer = window.setInterval(tick, 40);
});

onUnmounted(() => {
  if (timer != null) {
    window.clearInterval(timer);
    timer = null;
  }
});
</script>

<style scoped>
.loading-page {
  position: fixed;
  inset: 0;
  z-index: 9999;
  /* Desktop: contain so the landscape 2400x1350 hero is never cropped (logo +
     person + features all visible). The backing gradient blends the leftover
     space top/bottom on portrait viewports.
     Mobile: cover. The mobile artwork is portrait (853x1844) with the logo
     centered, so cropping a few px off the safe edges fills the screen with
     no visible "T" loss — solves the side-band issue real phones reported. */
  background-color: #e8eefc;
  background-repeat: no-repeat;
  background-size: contain;
  background-position: center center;
  overflow: hidden;
}
.loading-page.is-mobile {
  background-size: cover;
}

.status {
  position: absolute;
  z-index: 2;
  /* PC: tucked under the Telemedicine logo + caption (left side of artwork) */
  top: 62%;
  left: 22%;
  transform: translate(-50%, 0);
  display: grid;
  grid-template-columns: auto 1fr auto;
  align-items: center;
  gap: 8px 12px;
  width: min(calc(100% - 32px), 320px);
  padding: 10px 16px;
  background: rgba(255, 255, 255, 0.92);
  backdrop-filter: blur(10px);
  border-radius: 14px;
  box-shadow: 0 10px 28px rgba(30, 64, 175, 0.25);
}
.is-mobile .status {
  top: auto;
  bottom: 8vh;
  left: 50%;
  transform: translateX(-50%);
}

.dot-spinner {
  display: flex;
  gap: 5px;
}
.dot-spinner span {
  width: 7px;
  height: 7px;
  border-radius: 50%;
  background: var(--tm-primary);
  animation: bounce 1.2s ease-in-out infinite both;
}
.dot-spinner span:nth-child(1) {
  animation-delay: -0.32s;
}
.dot-spinner span:nth-child(2) {
  animation-delay: -0.16s;
  background: var(--tm-primary-deep);
}
.dot-spinner span:nth-child(3) {
  background: var(--tm-primary-light);
}

@keyframes bounce {
  0%,
  80%,
  100% {
    transform: scale(0.55);
    opacity: 0.6;
  }
  40% {
    transform: scale(1);
    opacity: 1;
  }
}

.status-text {
  font-size: 12px;
  font-weight: 700;
  color: var(--tm-primary-deep);
  letter-spacing: 0.2px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
.percent {
  font-size: 14px;
  font-weight: 800;
  color: var(--tm-primary-deep);
  font-variant-numeric: tabular-nums;
  min-width: 38px;
  text-align: right;
}
.progress-track {
  grid-column: 1 / -1;
  width: 100%;
  height: 5px;
  border-radius: 999px;
  background: var(--tm-blue-100);
  overflow: hidden;
}
.progress-fill {
  height: 100%;
  border-radius: 999px;
  background: var(--tm-gradient);
  transition: width 80ms linear;
  box-shadow: 0 0 8px rgba(37, 99, 235, 0.4);
}

@media (max-width: 768px) {
  .status {
    max-width: 280px;
    padding: 8px 14px;
  }
}

/* Narrow desktop / split window: nudge the status closer to centre so it
   stays under the (smaller) logo position. */
@media (min-width: 769px) and (max-width: 1280px) {
  .loading-page:not(.is-mobile) .status {
    top: 60%;
    left: 26%;
  }
}
</style>
